import { create } from 'zustand';
import type { IntentPayloads } from '@lib/overlay/intents';
import type { CelebrationKind } from '@utils/celebrationMarker';

/** The celebration on screen: the intent payload that asked for it, kind and all. */
export type OpenCelebration = IntentPayloads['celebration'];

/**
 * CelebrationHost's open-state (overlay-system-design §6.2 rule 3).
 *
 * A zustand write reaches useSyncExternalStore on the sync lane whatever
 * started it, so a grant continuation's store write commits before paint. A
 * celebration-to-celebration swap is click-started and would commit in one pass
 * with useState too; the store is for swaps started outside a user event (a
 * programmatic close, a timer, a takeover clearing), which M2's cross-surface
 * queue brings.
 *
 * It holds the intent's payload union, so what a drawer renders (the ticketed drawer's
 * `registration`, the PIN sheet's `eventPin`) can only be there with its kind.
 */
interface CelebrationState {
  /** Which celebration is on screen, with what it renders and its arrival attribution (`source`). */
  open: OpenCelebration | null;
  show: (open: OpenCelebration) => void;
  /** Kind-guarded so a late close continuation can never clobber a newer celebration. */
  clear: (kind: CelebrationKind) => void;
}

export const useCelebrationStore = create<CelebrationState>()(set => ({
  open: null,
  show: open => {
    set({ open });
  },
  clear: kind => {
    set(state => (state.open?.kind === kind ? { open: null } : state));
  },
}));
